import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@design-system/atoms/icon';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import * as S from './ScrollToTopButton.styles';

const SCROLL_THRESHOLD_MULTIPLIER = 1.5;

/** Hide when any modal/drawer overlay is open (they use data-overlay-layer) */
function useOverlayOpen() {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  useEffect(() => {
    const check = () => setIsOverlayOpen(document.querySelectorAll('[data-overlay-layer]').length > 0);
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return isOverlayOpen;
}

export const ScrollToTopButton = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [activeTarget, setActiveTarget] = useState<HTMLElement | Window | null>(null);
  const isOverlayOpen = useOverlayOpen();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    setActiveTarget(null);
    setIsVisible(false);
    setIsAtBottom(false);

    const getScrollMetrics = (target: HTMLElement | Window) => {
      if (target === window) {
        const { documentElement } = document;
        return {
          scrollTop: documentElement.scrollTop || document.body.scrollTop,
          scrollHeight: documentElement.scrollHeight,
          clientHeight: window.innerHeight || documentElement.clientHeight,
        };
      }

      if (target instanceof HTMLElement) {
        return {
          scrollTop: target.scrollTop,
          scrollHeight: target.scrollHeight,
          clientHeight: target.clientHeight,
        };
      }

      return { scrollTop: 0, scrollHeight: 0, clientHeight: 0 };
    };

    const updateState = (target: HTMLElement | Window) => {
      const { scrollTop, clientHeight, scrollHeight } = getScrollMetrics(target);
      const isScrollable = scrollHeight > clientHeight;

      if (!isScrollable) {
        if (target === window) {
          setIsVisible(false);
          setIsAtBottom(false);
        }
        return;
      }

      setActiveTarget(target);
      setIsVisible(scrollTop > clientHeight * SCROLL_THRESHOLD_MULTIPLIER);
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 48);
    };

    const listeners = new Map<HTMLElement | Window, () => void>();

    const attachListener = (target: HTMLElement | Window) => {
      if (listeners.has(target)) {
        return;
      }

      const handler = () => updateState(target);
      target.addEventListener('scroll', handler, { passive: true });
      listeners.set(target, handler);
      handler();
    };

    attachListener(window);

    const containers = Array.from(document.querySelectorAll<HTMLElement>('[data-scroll-container="true"]'));
    containers.forEach((container) => attachListener(container));

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node.dataset.scrollContainer === 'true') {
            attachListener(node);
          }

          const nested = node.querySelectorAll<HTMLElement>('[data-scroll-container="true"]');
          nested.forEach((element) => attachListener(element));
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      listeners.forEach((handler, target) => {
        target.removeEventListener('scroll', handler);
      });
      observer.disconnect();
    };
  }, [location.pathname]);

  const handleScrollToTop = useCallback(() => {
    const target = activeTarget || window;

    if (target === window) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      return;
    }

    target.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

  }, [activeTarget]);

  const label = formatMessage({ id: 'common.button.scrollToTop' });

  return (
    <S.Container
      type="button"
      onClick={handleScrollToTop}
      aria-label={label}
      title={label}
      isVisible={isVisible && !isOverlayOpen}
      isAtBottom={isAtBottom}
    >
      <Icon name="arrowUp" size={16} />
      <span>{label}</span>
    </S.Container>
  );
};

export default ScrollToTopButton;
